import React, { useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Alert, Form, Modal, Typography } from 'antd';
import { FormikProvider, useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';

import { ClosureModal } from './ClosureModal';
import { useCreatePartnerClosureRequest } from '../../api/createPartnerClosureRequest';
import { useDeletePartnerClosureRequest } from '../../api/deletePartnerClosureRequest';
import { usePartnerClosureRequest } from '../../api/getPartnerClosureRequest';
import { usePartnerStats } from '../../api/getPartnerStats';
import { closureRequestStatuses, Reason, reasons } from '../../types';
import { ShieldExclamationIcon } from '../Icons/ShieldExclamationIcon';
import { SuccessfullClosureApprovedToast } from '../SuccessfullClosureApprovedToast';
import ScopedCta from '@/components/ScopedCta';
import { scopes } from '@/config';
import useDeletePartner from '@/features/lp-village/partners/hooks/useDeletePartner';
import useError from '@/hooks/useError';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

const { Text } = Typography;

const validateForm =
  ({ partnerId, canClosePartner }) =>
  (values) => {
    const errors: any = {};

    if (!values.reason) {
      errors.reason = 'Please select a reason for closure before proceeding.';
    }

    if (
      values.reason !== reasons.CreatedByError &&
      canClosePartner &&
      values.confirmPartnerId !== partnerId
    ) {
      errors.confirmPartnerId = 'Error';
    }

    return errors;
  };

export type FormData = {
  reason?: Reason;
  details?: string;
  confirmPartnerId?: string;
};

export const StartOrCancelClosureProcedure = () => {
  const { id: partnerId } = useParams() as { id: string };
  const navigate = useNavigate();

  const addError = useError();
  const addSuccess = useToasts((state) => state.addItem);

  const [open, setOpen] = useState(false);
  const [openCancelConfirmationModal, setOpenCancelConfirmationModal] =
    useState(false);

  // Get partner STATS
  const {
    data: partnerStats,
    isPending: isPartnerStatsPending,
    isError: isPartnerStatsError,
  } = usePartnerStats({ config: { enabled: open } });

  // Get closure request
  const {
    data: closureRequest,
    isPending: isExistingClosureRequestPending,
    isError: errorGettingClosingRequest,
  } = usePartnerClosureRequest();

  // Create closure request with OPENED status
  const {
    mutateAsync: createClosureRequest,
    isPending: isCreateClosureRequestLoading,
    isError: isCreateClosureRequestError,
    reset,
  } = useCreatePartnerClosureRequest();

  // Delete closure request
  const {
    mutateAsync: cancelClosureRequest,
    isPending: isCancelClosureRequestLoading,
  } = useDeletePartnerClosureRequest();

  // Really delete partner mutation
  const [
    { loading: isDeletePartnerLoading, error: isDeletePartnerError },
    deletePartner,
  ] = useDeletePartner(partnerId);

  const onSubmit = async (values: FormData) => {
    const isValid = await formik.validateForm();

    if (!isValid) {
      return;
    }

    const createdByError = values.reason === reasons.CreatedByError;
    const partnerHasNoWarnings = partnerStats?.canClosePartner;

    // Really DELETE the partner if the closure request is created by error
    if (createdByError) {
      const response = await deletePartner();

      if (response.success) {
        navigate('/lp-village/partners');
      }

      return;
    } else if (partnerHasNoWarnings) {
      // Create the closure request with the status: "APPROVED"
      // so that anonymization can start in 3 months
      try {
        await createClosureRequest({
          ...(values as any),
          status: closureRequestStatuses.Approved,
        });

        addSuccess(
          {
            msg: <SuccessfullClosureApprovedToast />,
            type: MessageType.Success,
          },
          'root',
          {
            duration: 7500,
          }
        );
      } catch (error) {
        addError(
          'Error while closing this organization, please retry or contact support'
        );
      }
    } else {
      // Create closure request
      try {
        await createClosureRequest({
          ...(values as any),
          status: closureRequestStatuses.Opened,
        });

        addSuccess(
          {
            msg: 'Closure request created successfully.',
            type: MessageType.Success,
          },
          'root',
          {
            duration: 7500,
          }
        );
      } catch (error) {
        addError(
          'Error while requesting closure, please retry or contact support'
        );
      }
    }

    handleCancel();
  };

  const formik = useFormik<FormData>({
    initialValues: {},
    validateOnChange: false,
    validate: validateForm({
      partnerId,
      canClosePartner: partnerStats?.canClosePartner,
    }),
    onSubmit,
  });

  const showCancelClosureRequestModal = () => {
    setOpenCancelConfirmationModal(true);
  };

  const showCreateClosureRequestModal = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    // Clear formik state
    formik.resetForm();
    // Reset mutation (closureRequest) state
    reset();
  };

  const handleOk = () => {
    formik.handleSubmit();
  };

  const onButtonClick = async () => {
    if (errorGettingClosingRequest) {
      return;
    }

    // If there is an existing closure request, we want to cancel it
    if (closureRequest) {
      showCancelClosureRequestModal();

      return;
    }

    // Otherwise, we want to show the modal to start the closure procedure
    showCreateClosureRequestModal();

    return;
  };

  const isPartnerAnonymized =
    closureRequest?.status === closureRequestStatuses.Anonymized;

  const isButtonDisabled =
    isExistingClosureRequestPending ||
    errorGettingClosingRequest ||
    isPartnerAnonymized;

  return (
    <>
      <ScopedCta
        component={Text}
        style={{
          display: 'inline-flex',
          gap: '0.5rem',
          alignItems: 'center',
          color: 'red',
          opacity: isButtonDisabled ? 0.5 : 1,
        }}
        type="text"
        disabled={isButtonDisabled}
        data-testid={
          closureRequest ? 'cancel-closure-request' : 'start-closure-request'
        }
        onClick={isButtonDisabled ? null : onButtonClick}
        requiredScopes={[scopes.MANAGE_LPVILLAGE_PARTNER]}
      >
        {isExistingClosureRequestPending ? (
          <LoadingOutlined />
        ) : (
          <ShieldExclamationIcon />
        )}
        {closureRequest?.status === closureRequestStatuses.Opened
          ? 'Cancel closure request'
          : 'Start the closure procedure'}
      </ScopedCta>
      {/* Create Closure Request Modal */}
      <Modal
        destroyOnClose
        open={open}
        onOk={handleOk}
        confirmLoading={
          formik.isSubmitting ||
          isCreateClosureRequestLoading ||
          isDeletePartnerLoading
        }
        okText={
          formik.values.reason === reasons.CreatedByError ||
          partnerStats?.canClosePartner
            ? 'Close this organisation'
            : 'Start the closure procedure'
        }
        onCancel={handleCancel}
        cancelButtonProps={{
          disabled: formik.isSubmitting,
          'data-testid': 'cancel-closure',
        }}
        okButtonProps={{
          danger: true,
          disabled: isPartnerStatsPending || isPartnerStatsError,
          'data-testid': 'submit-closure',
        }}
        width={{
          xs: '90%',
          sm: '80%',
          md: '70%',
          lg: '60%',
          xl: '41.25rem',
          xxl: '41.25rem',
        }}
        styles={{
          content: {
            position: 'relative',
            overflow: 'hidden',
            padding: '2rem',
          },
          footer: {
            marginTop: '2rem',
          },
          wrapper: {
            display: 'flex',
            justifyContent: 'center',
          },
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '13.25rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#FFE6E5',
            borderRadius: '50%',
            // Almost random width and height (chosen by eye)
            width: '206rem',
            height: '447rem',
          }}
        ></div>
        <FormikProvider value={formik}>
          <Form component="form" onFinish={formik.handleSubmit}>
            <ClosureModal />
            {(isCreateClosureRequestError || isDeletePartnerError) && (
              <Alert
                showIcon
                type="error"
                message="Error while requesting closure"
                description={
                  <Text>
                    Partnership closure request failed. Please check system logs
                    or contact support.
                  </Text>
                }
                style={{
                  width: '100%',
                  marginTop: '1rem',
                }}
              />
            )}
          </Form>
        </FormikProvider>
      </Modal>
      {/* Cancel Closure Request Confirmation Modal */}
      <Modal
        destroyOnClose
        open={openCancelConfirmationModal}
        onOk={async () => {
          try {
            await cancelClosureRequest();

            addSuccess(
              {
                msg: 'Closure request cancelled successfully.',
                type: MessageType.Success,
              },
              'root',
              {}
            );
          } catch (error) {
            addError(
              'Error while cancelling closure request, please retry or contact support'
            );
          }

          setOpenCancelConfirmationModal(false);
        }}
        okButtonProps={{
          danger: true,
          disabled: isCancelClosureRequestLoading,
          loading: isCancelClosureRequestLoading,
          'data-testid': 'submit-cancel-closure',
        }}
        onCancel={() => setOpenCancelConfirmationModal(false)}
      >
        <Text>Are you sure you want to cancel the closure request?</Text>
      </Modal>
    </>
  );
};
